import { Typography } from '@material-ui/core';
import React from 'react';
import { ArrowRight } from 'lucide-react';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import { CtaButton } from '../CtaButton';
import { Headline } from '../Headline';
import { Reveal } from '../motion';
import { AgenticMontage } from './AgenticMontage';
import { GradientBackground } from './GradientBackground';

const HeroSectionWrapper = styled('section')`
  position: relative;
  overflow: hidden;
  padding: 80px 0 64px;
  display: flex;
  flex-direction: column;

  @media (max-width: 960px) {
    padding: 60px 0 48px;
  }
`;

const HeroContentWrapper = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  min-height: 620px;

  @media (max-width: 960px) {
    min-height: auto;
  }
`;

const HeroWrapper = styled('div')`
  max-width: 1200px;
  margin: 0 auto;
  padding: 48px 24px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  position: relative;
  z-index: 1;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const LeftContent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const KickerLine = styled('div')`
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${COLORS.PRIMARY};
`;

const Description = styled(Typography)`
  font-size: 18px;
  line-height: 1.6;
  color: #4f4f4f;
  margin: 0;

  strong {
    color: #0e1c2b;
  }
` as typeof Typography;

const ButtonGroup = styled('div')`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const LearnMoreLink = styled('a')`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${COLORS.PRIMARY};
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const RightContent = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const HeroSection = ({ onGetStarted }: { onGetStarted: () => void }) => {
  return (
    <HeroSectionWrapper>
      <GradientBackground />
      <HeroContentWrapper>
        <HeroWrapper>
          <LeftContent>
            <Reveal>
              <KickerLine>Agentic commerce infrastructure</KickerLine>
            </Reveal>
            <Reveal delay={100}>
              <Headline component="h1">
                The agentic commerce platform for publishers and brands
              </Headline>
            </Reveal>

            <Reveal delay={200}>
              <Description component="p">
                Affilimate runs on your first-party commerce data to{' '}
                <strong>maximize yield from every pageview</strong>,{' '}
                <strong>monetize your influence in AI answers</strong>, and{' '}
                <strong>accelerate revenue through automation</strong>. Your team
                reviews every campaign before it goes live.
              </Description>
            </Reveal>

            <Reveal delay={300}>
              <ButtonGroup>
                <CtaButton size="medium" onClick={onGetStarted}>
                  Get a demo
                </CtaButton>
                <LearnMoreLink href="/ai-platform/">
                  Explore the AI platform <ArrowRight size={18} />
                </LearnMoreLink>
              </ButtonGroup>
            </Reveal>
          </LeftContent>

          <RightContent>
            <Reveal delay={250} distance={32}>
              <AgenticMontage />
            </Reveal>
          </RightContent>
        </HeroWrapper>
      </HeroContentWrapper>
    </HeroSectionWrapper>
  );
};
